import type { AxiosResponse } from 'axios'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { apiAxios } from '@/api'
import { MARK_ACTIVITY_REPORTED_PATH } from '../../lib/constants'
import type { WidgetDisplay } from '../../lib/types'
import {
  CATCH_UP_MONTH_STATE,
  fetchCatchUpMonthStates,
  MONTH_PATTERN,
  slotKey,
  toMonthDisplay,
} from './statementUpload'

export interface EmptyMonthsInput {
  financialAccountId: number
  accountLabel: string
  months: string[]
  remainingCount: number
}

// Matched without the label, which the redactor may rewrite.
const NO_ACTIVITY_PREFIX = 'No practice activity on '
const ACTIVITY_PREFIX = 'Had practice activity on '
const NOT_CONFIRMABLE_PREFIX = 'Nothing to confirm on '

const toDate = (month: string): DateTime =>
  DateTime.fromFormat(month, DATE_FORMATS_LUXON.YEAR_MONTH)

export const parseEmptyMonthsInput = (
  input: Record<string, unknown>
): EmptyMonthsInput | null => {
  const { financialAccountId, accountLabel, months, remainingCount } = input
  if (
    typeof financialAccountId !== 'number' ||
    typeof accountLabel !== 'string' ||
    !Array.isArray(months)
  )
    return null
  const valid = months.filter(
    (month): month is string =>
      typeof month === 'string' && MONTH_PATTERN.test(month)
  )
  if (valid.length === 0) return null
  return {
    financialAccountId,
    accountLabel,
    months: [...new Set(valid)].sort(),
    remainingCount:
      typeof remainingCount === 'number' && remainingCount > 0
        ? Math.floor(remainingCount)
        : 0,
  }
}

export const formatMonthRange = (months: string[]): string => {
  const first = months[0]
  const last = months[months.length - 1]
  if (first === last) return toMonthDisplay(first)
  const start = toDate(first)
  const end = toDate(last)
  const startLabel =
    start.year === end.year
      ? start.toFormat(DATE_FORMATS_LUXON.MONTH)
      : toMonthDisplay(first)
  return `${startLabel} through ${toMonthDisplay(last)}`
}

export interface MonthChip {
  month: string
  /** Short, since the year sits in the group heading. */
  label: string
  /** Read by assistive tech, which cannot see the heading a chip sits under. */
  name: string
}

export interface MonthChipGroup {
  /** Null when every month falls in one year, so no heading is needed. */
  year: string | null
  chips: MonthChip[]
}

export const toChipGroups = (months: string[]): MonthChipGroup[] => {
  const byYear = new Map<string, MonthChip[]>()
  months.forEach((month) => {
    const date = toDate(month)
    const year = date.toFormat(DATE_FORMATS_LUXON.YEAR_FOUR_DIGIT)
    byYear.set(year, [
      ...(byYear.get(year) ?? []),
      {
        month,
        label: date.toFormat(DATE_FORMATS_LUXON.MONTH_ABBR),
        name: toMonthDisplay(month),
      },
    ])
  })
  const spansYears = byYear.size > 1
  return [...byYear].map(([year, chips]) => ({
    year: spansYears ? year : null,
    chips,
  }))
}

const rangePhrase = (months: string[]): string =>
  `${months.length === 1 ? 'in' : 'from'} ${formatMonthRange(months)}`

export const toQuestion = (months: string[]): string =>
  `Did you see any clients or pay for anything for the practice ${rangePhrase(months)}?`

const isCalendarRun = (months: string[]): boolean =>
  months.every(
    (month, index) =>
      index === 0 ||
      toDate(months[index - 1])
        .plus({ months: 1 })
        .hasSame(toDate(month), 'month')
  )

/** Names each month when the run has a gap, so the summary can count what was recorded. */
const monthsLabel = (months: string[]): string => {
  const sorted = [...months].sort()
  return isCalendarRun(sorted)
    ? formatMonthRange(sorted)
    : sorted.map(toMonthDisplay).join(', ')
}

const monthsPhrase = (months: string[]): string =>
  months.length === 1 || !isCalendarRun([...months].sort())
    ? `in ${monthsLabel(months)}`
    : `from ${monthsLabel(months)}`

export const noActivityOutcome = (
  accountLabel: string,
  months: string[]
): string => `${NO_ACTIVITY_PREFIX}${accountLabel} ${monthsPhrase(months)}.`

export const activityOutcome = (
  accountLabel: string,
  picked: string[]
): string =>
  `${ACTIVITY_PREFIX}${accountLabel} in ${[...picked].sort().map(toMonthDisplay).join(', ')}.`

const HAS_WHAT_IT_NEEDS = 'Heard has what it needs for these months'

/** Names only the asked months that still need a statement, since others may be caught up. */
export const notConfirmableOutcome = (
  accountLabel: string,
  stillNeeded: string[]
): string =>
  stillNeeded.length === 0
    ? `${NOT_CONFIRMABLE_PREFIX}${accountLabel}: ${HAS_WHAT_IT_NEEDS}.`
    : `${NOT_CONFIRMABLE_PREFIX}${accountLabel}: Heard still needs statements ${monthsPhrase(stillNeeded)}.`

const stillNeededLabel = (stillNeeded: string[]): string => {
  if (stillNeeded.length === 0) return HAS_WHAT_IT_NEEDS
  return stillNeeded.length === 1
    ? `${monthsLabel(stillNeeded)} still needs a statement`
    : `${monthsLabel(stillNeeded)} still need statements`
}

export const notConfirmableDisplay = (
  accountLabel: string,
  stillNeeded: string[]
): WidgetDisplay => ({
  label: stillNeededLabel(stillNeeded),
  meta: accountLabel,
})

export const isNoActivityOutcome = (outcome: string | undefined): boolean =>
  Boolean(outcome?.startsWith(NO_ACTIVITY_PREFIX))

export const isNotConfirmableOutcome = (outcome: string | undefined): boolean =>
  Boolean(outcome?.startsWith(NOT_CONFIRMABLE_PREFIX))

export interface AskableMonths {
  /** The asked months the status still lets the member clear, so the card never asks about any other. */
  confirmable: string[]
  /** The asked months still waiting for a statement. */
  stillNeeded: string[]
}

export const fetchAskableMonths = async (
  financialAccountId: number,
  months: string[]
): Promise<AskableMonths> => {
  const states = await fetchCatchUpMonthStates()
  const stateOf = (month: string) =>
    states.get(slotKey(financialAccountId, month))
  return {
    confirmable: months.filter(
      (month) => stateOf(month)?.confirmableAsEmpty === true
    ),
    stillNeeded: months.filter(
      (month) =>
        stateOf(month)?.state === CATCH_UP_MONTH_STATE.awaitingStatement
    ),
  }
}

/** The months an activity answer named, so a reload can redraw them picked. */
export const pickedFromOutcome = (
  outcome: string | undefined,
  months: string[]
): string[] | null =>
  outcome?.startsWith(ACTIVITY_PREFIX)
    ? months.filter((month) => outcome.includes(toMonthDisplay(month)))
    : null

/** The asked months a no-activity answer covered: the card records only those it could still clear. */
export const answeredNoActivityMonths = (
  outcome: string | undefined,
  months: string[]
): string[] => {
  for (let start = 0; start < months.length; start++)
    for (let end = months.length; end > start; end--) {
      const run = months.slice(start, end)
      if (outcome?.endsWith(` ${rangePhrase(run)}.`)) return run
    }
  const named = months.filter((month) =>
    outcome?.includes(toMonthDisplay(month))
  )
  return named.length > 0 ? named : months
}

export const toSummary = (monthCount: number): string =>
  `${monthCount} ${monthCount === 1 ? 'month' : 'months'} with no practice activity`

/**
 * Records the months that can still be cleared on the member's answer as needing
 * no statement. One settled another way since the question, such as by a
 * statement, needs nothing.
 */
export const recordNoActivity = async (
  financialAccountId: number,
  months: string[]
): Promise<void> => {
  const { confirmable: stillOpen } = await fetchAskableMonths(
    financialAccountId,
    months
  )
  if (stillOpen.length === 0) return
  await apiAxios.put(
    '/finances/api/v1/user_documents/statement/mark_not_expected',
    { financialAccountId, statementMonths: stillOpen, notExpected: true }
  )
}

interface ActivityAnswerRequest {
  financialAccountId: number
  askedMonths: string[]
  /** The months the member picked; each one is also in `askedMonths`. */
  activityMonths: string[]
}

interface ActivityAnswerResponse {
  activityReported: string[]
  noStatementExpected: string[]
}

/**
 * The server splits the asked months at the earliest picked one, and keeps only
 * those still open to the question.
 */
export const recordActivity = async (request: ActivityAnswerRequest) => {
  const { data } = await apiAxios.put<
    ActivityAnswerResponse,
    AxiosResponse<ActivityAnswerResponse>,
    ActivityAnswerRequest
  >(MARK_ACTIVITY_REPORTED_PATH, request)
  return data
}
